import { createContext, useContext, useReducer, useEffect, useCallback, useRef, useState, type ReactNode } from 'react'
import type { User, AuthState, Permission, UserRole } from '@/features/auth/types/auth'
import { ROLE_PERMISSIONS } from '@/features/auth/types/auth'

// Secure Session Manager for proper session isolation
class SecureSessionManager {
  private static instance: SecureSessionManager
  private sessionId: string | null = null
  
  private constructor() {
    // Restore session ID from storage on reload
    try {
      const sid = sessionStorage.getItem('session_id')
      if (sid) {
        this.sessionId = sid
        console.log('🔍 SecureSessionManager: Restored session ID:', sid.substring(0, 8) + '...')
      } else {
        console.log('🔍 SecureSessionManager: No existing session ID found')
      }
    } catch (error) {
      console.warn('🔍 SecureSessionManager: SessionStorage error:', error)
      // Ignore sessionStorage errors (e.g., in incognito mode)
    }
  }
  
  static getInstance(): SecureSessionManager {
    if (!SecureSessionManager.instance) {
      SecureSessionManager.instance = new SecureSessionManager()
    }
    return SecureSessionManager.instance
  }
  
  // Generate cryptographically secure session ID
  generateSessionId(): string {
    try {
      const array = new Uint8Array(32)
      crypto.getRandomValues(array)
      return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('')
    } catch (error) {
      // Fallback for environments without crypto.getRandomValues
      console.warn('crypto.getRandomValues not available, using fallback')
      return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 16)
    }
  }
  
  // Store session securely (server handles HttpOnly cookies)
  storeSession(sessionId: string, user: User): void {
    console.log('🔍 SecureSessionManager: Storing session for user:', user.email)
    this.sessionId = sessionId
    
    // Only store non-sensitive data client-side
    try {
      sessionStorage.setItem('session_id', sessionId)
      sessionStorage.setItem('user_id', user.id.toString())
      sessionStorage.setItem('user_role', user.role)
      console.log('🔍 SecureSessionManager: Session stored successfully')
      
      // Clear any previous user data to prevent leakage
      this.clearPreviousUserData(user.id)
    } catch (error) {
      console.warn('Failed to store session in sessionStorage:', error)
      // Continue without session storage if not available
    }
  }
  
  // Clear session completely
  clearSession(): void {
    this.sessionId = null
    sessionStorage.clear()
    
    // Clear sensitive data from localStorage
    const keysToRemove = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && (key.includes('auth') || key.includes('user') || key.includes('token'))) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key))
    
    // Clear browser cache if supported
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name))
      })
    }
  }
  
  // Clear data from previous user sessions
  private clearPreviousUserData(currentUserId: number): void {
    const keysToRemove = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith('user_') && !key.startsWith(`user_${currentUserId}_`)) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key))
  }
  
  // Validate session integrity
  isSessionValid(): boolean {
    const sid = sessionStorage.getItem('session_id')
    // Consider valid if a session id exists and looks like a token
    return !!(sid && sid.length >= 32)
  }
  
  // Get current session ID
  getCurrentSessionId(): string | null {
    if (!this.sessionId) {
      try { 
        this.sessionId = sessionStorage.getItem('session_id') 
      } catch (error) {
        // Ignore sessionStorage errors
      }
    }
    return this.sessionId
  }
}

// Auth Actions
type AuthAction = 
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; token: string; sessionId: string } }
  | { type: 'LOGIN_FAILURE' }
  | { type: 'LOGOUT' }
  | { type: 'UPDATE_USER'; payload: Partial<User> }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'OTP_REQUIRED'; payload: { userId: number; email: string; expiresIn: number } }
  | { type: 'OTP_CANCEL' }

// OTP state for new device verification
interface OTPState {
  required: boolean
  userId: number | null
  email: string
  expiresIn: number
  deviceFingerprint: string
  sessionId: string
  password: string // Temporarily stored for retry after OTP
}

// Initial state
const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  token: undefined
}

// Initial OTP state
const initialOTPState: OTPState = {
  required: false,
  userId: null,
  email: '',
  expiresIn: 0,
  deviceFingerprint: '',
  sessionId: '',
  password: ''
}

// Auth reducer
function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'LOGIN_START':
      return { ...state, isLoading: true }
    
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false
      }
    
    case 'LOGIN_FAILURE':
      return {
        ...initialState,
        isLoading: false
      }
    
    case 'LOGOUT':
      return initialState
    
    case 'UPDATE_USER':
      return {
        ...state,
        user: state.user ? { ...state.user, ...action.payload } : null
      }
    
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload }
    
    case 'OTP_REQUIRED':
    case 'OTP_CANCEL':
      // These are handled by OTP state reducer, not auth state
      return state
    
    default:
      return state
  }
}

// Auth context interface
interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string; requiresOTP?: boolean }>
  logout: () => Promise<void>
  hasPermission: (permission: Permission) => boolean
  hasAnyPermission: (permissions: Permission[]) => boolean
  hasRole: (role: UserRole | UserRole[]) => boolean
  canAccessRoute: (path: string) => boolean
  updateUser: (userData: Partial<User>) => void
  refreshToken: () => Promise<void>
  // OTP related
  otpState: OTPState
  verifyOTP: (otp: string) => Promise<{ success: boolean; error?: string }>
  cancelOTP: () => void
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Auth provider component
interface AuthProviderProps {
  children: ReactNode
}

// Generate stable device fingerprint from browser characteristics
// This fingerprint should remain constant for the same device/browser
function generateDeviceFingerprint(): string {
  try {
    // First, check if we have a stored fingerprint (trusted device)
    const storedFingerprint = localStorage.getItem('tw_device_fingerprint')
    if (storedFingerprint) {
      console.log('🔍 Using stored device fingerprint:', storedFingerprint.substring(0, 16) + '...')
      return storedFingerprint
    }
    
    // Generate new fingerprint from stable browser characteristics only
    const components = [
      navigator.userAgent,
      navigator.language,
      screen.width + 'x' + screen.height,
      screen.colorDepth,
      new Date().getTimezoneOffset(),
      navigator.hardwareConcurrency || 0,
      navigator.platform
    ]
    
    const combined = components.join('|')
    
    // Use a proper hash function for fingerprint
    let hash = 0
    for (let i = 0; i < combined.length; i++) {
      const char = combined.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash // Convert to 32bit integer
    }
    
    // Convert to hex string and pad - NO TIMESTAMP!
    const fingerprint = 'dev-' + Math.abs(hash).toString(16).padStart(8, '0')
    
    console.log('🔍 Generated new device fingerprint:', fingerprint)
    return fingerprint
  } catch (error) {
    console.warn('Failed to generate device fingerprint:', error)
    // Use a fallback that's still stable per session
    const fallback = localStorage.getItem('tw_device_fingerprint_fallback')
    if (fallback) return fallback
    
    const newFallback = 'fallback-' + Math.random().toString(36).substr(2, 16)
    localStorage.setItem('tw_device_fingerprint_fallback', newFallback)
    return newFallback
  }
}

// Store device fingerprint after successful OTP verification (trust this device)
function storeTrustedDeviceFingerprint(fingerprint: string): void {
  try {
    localStorage.setItem('tw_device_fingerprint', fingerprint)
    console.log('✅ Device fingerprint stored as trusted:', fingerprint.substring(0, 16) + '...')
  } catch (error) {
    console.warn('Failed to store device fingerprint:', error)
  }
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(authReducer, initialState)
  const [otpState, setOtpState] = useState<OTPState>(initialOTPState)
  const sessionManager = useRef(SecureSessionManager.getInstance())
  const activityTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastActivityRefresh = useRef<number>(0)
  
  // Session timeout configuration
  // Only logout after 1 hour of inactivity - active users stay logged in indefinitely
  const INACTIVITY_TIMEOUT = 60 * 60 * 1000 // 1 hour idle timeout
  
  // Initialize authentication from secure session
  useEffect(() => {
    const initAuth = async () => {
      try {
        // Check for existing session
        const sessionId = sessionStorage.getItem('session_id')
        
        if (!sessionId || !sessionManager.current.isSessionValid()) {
          dispatch({ type: 'SET_LOADING', payload: false })
          return
        }
        
        // Validate session with server
        console.log('🔍 AuthContext: Validating existing session:', sessionId.substring(0, 8) + '...')
        
        const response = await fetch('/api/v2/auth.php/validate', {
          method: 'GET',
          credentials: 'include',
          headers: {
            'X-Session-ID': sessionId
          }
        })
        
        console.log('🔍 AuthContext: Validation response:', response.status, response.statusText)
        
        if (response.ok) {
          const data = await response.json()
          if (data.success && data.user) {
            dispatch({
              type: 'LOGIN_SUCCESS',
              payload: {
                user: data.user,
                token: data.token || '',
                sessionId: sessionId
              }
            })
            
            // Setup session monitoring
            setupSessionMonitoring()
          } else {
            // Invalid session, clear it
            sessionManager.current.clearSession()
            dispatch({ type: 'SET_LOADING', payload: false })
          }
        } else {
          // Server validation failed, clear session
          sessionManager.current.clearSession()
          dispatch({ type: 'SET_LOADING', payload: false })
        }
      } catch (error) {
        console.error('Auth initialization failed:', error)
        sessionManager.current.clearSession()
        dispatch({ type: 'SET_LOADING', payload: false })
      }
    }
    
    initAuth()
  }, [])
  
  // Setup session monitoring for inactivity timeout only (ADMIN ROUTES ONLY)
  // Active users stay logged in - only idle users get logged out after 1 hour
  // This only applies to admin routes - client side has no authentication
  const setupSessionMonitoring = useCallback(() => {
    // Only setup monitoring for admin routes - client side has no auth
    if (!window.location.pathname.startsWith('/admin')) {
      return () => {} // No cleanup needed for client routes
    }
    
    // Clear existing timer
    if (activityTimeoutRef.current) clearTimeout(activityTimeoutRef.current)
    
    // Inactivity timeout with server-side session refresh
    const resetInactivityTimeout = () => {
      if (activityTimeoutRef.current) clearTimeout(activityTimeoutRef.current)
      
      // Debounce server-side session refresh (max once every 5 minutes)
      const now = Date.now()
      if (now - lastActivityRefresh.current > 5 * 60 * 1000) {
        lastActivityRefresh.current = now
        // Refresh session on server in background to keep it alive
        const sessionId = sessionManager.current.getCurrentSessionId()
        if (sessionId) {
          fetch('/api/v2/auth.php/refresh', {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              'X-Session-ID': sessionId
            }
          }).catch(() => {}) // Ignore errors, this is just to extend session
        }
      }
      
      // Reset the inactivity timer - user will be logged out after 1 hour of no activity
      activityTimeoutRef.current = setTimeout(() => {
        console.log('⏰ Inactivity timeout (1 hour idle), logging out...')
        logout()
      }, INACTIVITY_TIMEOUT)
    }
    
    // Track user activity - comprehensive event tracking
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click', 'input', 'mousemove', 'focus']
    
    // Debounce the activity handler to prevent excessive calls
    let activityDebounceTimer: NodeJS.Timeout | null = null
    const debouncedResetActivity = () => {
      if (activityDebounceTimer) return
      activityDebounceTimer = setTimeout(() => {
        activityDebounceTimer = null
        resetInactivityTimeout()
      }, 1000) // Debounce for 1 second
    }
    
    events.forEach(event => {
      document.addEventListener(event, debouncedResetActivity, { passive: true })
    })
    
    // Start the initial inactivity timeout
    resetInactivityTimeout()
    
    return () => {
      if (activityDebounceTimer) clearTimeout(activityDebounceTimer)
      events.forEach(event => {
        document.removeEventListener(event, debouncedResetActivity)
      })
    }
  }, [])
  
  // Secure login function
  const login = useCallback(async (email: string, password: string): Promise<{ success: boolean; error?: string; requiresOTP?: boolean }> => {
    console.log('🔍 AuthContext: LOGIN FUNCTION CALLED')
    dispatch({ type: 'LOGIN_START' })
    
    const startTime = Date.now()
    const normalizedEmail = email.trim().toLowerCase()
    
    // Input validation
    if (!normalizedEmail || !password) {
      console.log('🔍 AuthContext: Validation failed - missing credentials')
      dispatch({ type: 'LOGIN_FAILURE' })
      return { success: false, error: 'Email and password are required' }
    }
    
    if (!normalizedEmail.includes('@')) {
      console.log('🔍 AuthContext: Validation failed - invalid email')
      dispatch({ type: 'LOGIN_FAILURE' })
      return { success: false, error: 'Please enter a valid email address' }
    }
    
    try {
      // Generate secure session ID
      let sessionId: string
      try {
        sessionId = sessionManager.current.generateSessionId()
        console.log('🔍 AuthContext: Session ID generated successfully:', sessionId.length, 'chars')
      } catch (error) {
        console.error('🔍 AuthContext: Session ID generation failed:', error)
        sessionId = 'fallback-' + Date.now() + '-' + Math.random().toString(36).substr(2, 16)
        console.log('🔍 AuthContext: Using fallback session ID')
      }
      
      // Generate device fingerprint for new device detection
      const deviceFingerprint = generateDeviceFingerprint()
      console.log('🔍 AuthContext: Device fingerprint generated:', deviceFingerprint.substring(0, 16) + '...')
      
      console.log(`🔐 AuthContext: Starting login process for ${normalizedEmail}`)
      
      const requestPayload = { 
        email: normalizedEmail, 
        password,
        sessionId,
        deviceFingerprint
      }
      
      console.log('🔍 AuthContext: Request payload prepared:', {
        email: requestPayload.email,
        hasPassword: !!requestPayload.password,
        passwordLength: requestPayload.password.length,
        hasSessionId: !!requestPayload.sessionId,
        sessionIdLength: requestPayload.sessionId.length,
        hasDeviceFingerprint: !!requestPayload.deviceFingerprint
      })
      
      const requestUrl = '/api/v2/auth.php/login'
      console.log('🔍 AuthContext: Making fetch request to:', requestUrl)
      
      const response = await fetch(requestUrl, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-ID': sessionId
        },
        body: JSON.stringify(requestPayload)
      })
      
      console.log('🔍 AuthContext: Fetch completed:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        type: response.type,
        url: response.url
      })
      
      const responseTime = Date.now() - startTime
      console.log(`⏱️ AuthContext: Response received in ${responseTime}ms`)
      
      // Robust JSON handling (supports empty/malformed bodies)
      let data: any = undefined
      try {
        // Lazy import to avoid circular deps in tests
        const { readJsonSafe } = await import('@/shared/lib/http')
        const parsed = await readJsonSafe(response)
        if (parsed.error) {
          console.warn('⚠️ AuthContext: Non-JSON or empty response:', parsed)
        }
        data = parsed.data ?? {}
        if (parsed.raw) {
          const preview = parsed.raw.length > 200 ? parsed.raw.slice(0, 200) + '…' : parsed.raw
          console.log('🔍 AuthContext: Raw response text:', preview)
        }
        console.log('🔍 AuthContext: Parsed response data (safe):', {
          success: data?.success,
          hasUser: !!data?.user,
          hasToken: !!data?.token,
          hasError: !!data?.error,
          requiresOTP: !!data?.requiresOTP
        })
      } catch (parseError) {
        console.error('❌ AuthContext: readJsonSafe threw unexpectedly', parseError)
        dispatch({ type: 'LOGIN_FAILURE' })
        return {
          success: false,
          error: 'Unexpected response from server. Please try again.'
        }
      }
      
      // Check if OTP is required (new device detected)
      if (data.requiresOTP) {
        console.log('🔐 AuthContext: New device detected, OTP verification required')
        
        // Store OTP state for verification
        setOtpState({
          required: true,
          userId: data.user_id,
          email: normalizedEmail,
          expiresIn: data.expires_in || 300,
          deviceFingerprint,
          sessionId,
          password // Temporarily store for retry after OTP (cleared after use)
        })
        
        dispatch({ type: 'SET_LOADING', payload: false })
        
        return { 
          success: false, 
          requiresOTP: true, 
          error: data.message || 'Please verify your identity with the code sent to your email.'
        }
      }
      
      if (response.ok && data.success) {
        console.log('🎉 AuthContext: LOGIN SUCCESS!')
        
        const { user, token } = data
        
        if (!user || !token) {
          console.error('❌ AuthContext: Success response missing user or token')
          dispatch({ type: 'LOGIN_FAILURE' })
          return { success: false, error: 'Invalid response from server' }
        }
        
        console.log('✅ AuthContext: User data received:', {
          id: user.id,
          email: user.email,
          name: user.name,
          role: user.role,
          tokenLength: token.length
        })
        
        // Store session securely
        try {
          sessionManager.current.storeSession(sessionId, user)
          console.log('✅ AuthContext: Session stored successfully')
        } catch (storageError) {
          console.error('❌ AuthContext: Failed to store session:', storageError)
          // Continue anyway - don't fail login due to storage issues
        }
        
        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: { user, token, sessionId }
        })
        
        // Setup session monitoring
        try {
          setupSessionMonitoring()
          console.log('✅ AuthContext: Session monitoring setup complete')
        } catch (monitorError) {
          console.warn('⚠️ AuthContext: Session monitoring setup failed:', monitorError)
          // Continue anyway
        }
        
        console.log('🏆 AuthContext: Login process completed successfully')
        return { success: true }
      } else {
        // Categorize authentication errors
        let errorMessage = data.error || 'Login failed'
        
        console.warn('❌ AuthContext: Login failed', {
          status: response.status,
          error: errorMessage,
          email: normalizedEmail
        })
        
        // Provide more specific error messages
        if (response.status === 401) {
          if (errorMessage.toLowerCase().includes('email')) {
            errorMessage = 'Account not found. Please check your email address.'
          } else if (errorMessage.toLowerCase().includes('password')) {
            errorMessage = 'Incorrect password. Please try again.'
          } else if (errorMessage.toLowerCase().includes('disabled')) {
            errorMessage = 'Your account has been disabled. Please contact support.'
          } else {
            errorMessage = 'Invalid email or password. Please check your credentials.'
          }
        } else if (response.status === 429) {
          errorMessage = 'Too many login attempts. Please wait a few minutes and try again.'
        } else if (response.status >= 500) {
          errorMessage = 'Server error occurred. Please try again later.'
        }
        
        dispatch({ type: 'LOGIN_FAILURE' })
        return { success: false, error: errorMessage }
      }
    } catch (error) {
      console.error('❌ AuthContext: Network error during login', error)
      
      let errorMessage = 'Connection failed. Please check your internet connection.'
      
      // Categorize network errors
      if (error instanceof TypeError && error.message.includes('fetch')) {
        errorMessage = 'Unable to connect to server. Please check your internet connection.'
      } else if (error instanceof Error) {
        if (error.name === 'AbortError') {
          errorMessage = 'Login request timed out. Please try again.'
        } else if (error.message.includes('CORS')) {
          errorMessage = 'Cross-origin request blocked. Please contact support.'
        }
      }
      
      dispatch({ type: 'LOGIN_FAILURE' })
      return { success: false, error: errorMessage }
    }
  }, [setupSessionMonitoring])
  
  // Secure logout function
  const logout = useCallback(async (): Promise<void> => {
    try {
      // Clear inactivity timer
      if (activityTimeoutRef.current) clearTimeout(activityTimeoutRef.current)
      
      // Notify server
      if (state.token) {
        await fetch('/api/v2/auth.php/logout', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-Session-ID': sessionManager.current.getCurrentSessionId() || ''
          }
        }).catch(() => {}) // Ignore errors
      }
    } finally {
      // Always clear local session
      sessionManager.current.clearSession()
      setOtpState(initialOTPState) // Clear OTP state on logout
      dispatch({ type: 'LOGOUT' })
      
      // Redirect to login if on admin route
      if (window.location.pathname.startsWith('/admin')) {
        window.location.href = '/admin/login'
      }
    }
  }, [state.token])
  
  // Verify OTP for new device login
  const verifyOTP = useCallback(async (otp: string): Promise<{ success: boolean; error?: string }> => {
    if (!otpState.required || !otpState.userId) {
      return { success: false, error: 'No OTP verification in progress' }
    }
    
    dispatch({ type: 'LOGIN_START' })
    
    try {
      const response = await fetch('/api/v2/auth.php/verify-otp', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-ID': otpState.sessionId
        },
        body: JSON.stringify({
          user_id: otpState.userId,
          otp,
          sessionId: otpState.sessionId,
          deviceFingerprint: otpState.deviceFingerprint
        })
      })
      
      const data = await response.json()
      
      if (response.ok && data.success) {
        console.log('🎉 AuthContext: OTP VERIFIED, LOGIN SUCCESS!')
        
        const { user, token } = data
        
        if (!user || !token) {
          dispatch({ type: 'LOGIN_FAILURE' })
          setOtpState(initialOTPState)
          return { success: false, error: 'Invalid response from server' }
        }
        
        // Store device fingerprint as trusted (this is the key fix!)
        storeTrustedDeviceFingerprint(otpState.deviceFingerprint)
        
        // Store session securely
        try {
          sessionManager.current.storeSession(otpState.sessionId, user)
        } catch (storageError) {
          console.error('❌ AuthContext: Failed to store session:', storageError)
        }
        
        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: { user, token, sessionId: otpState.sessionId }
        })
        
        // Clear OTP state (including stored password)
        setOtpState(initialOTPState)
        
        // Setup session monitoring
        try {
          setupSessionMonitoring()
        } catch (monitorError) {
          console.warn('⚠️ AuthContext: Session monitoring setup failed:', monitorError)
        }
        
        return { success: true }
      } else {
        dispatch({ type: 'SET_LOADING', payload: false })
        return { success: false, error: data.error || 'OTP verification failed' }
      }
    } catch (error) {
      console.error('❌ AuthContext: OTP verification error', error)
      dispatch({ type: 'SET_LOADING', payload: false })
      return { success: false, error: 'Connection failed. Please try again.' }
    }
  }, [otpState, setupSessionMonitoring])
  
  // Cancel OTP verification
  const cancelOTP = useCallback(() => {
    setOtpState(initialOTPState)
    dispatch({ type: 'LOGIN_FAILURE' })
  }, [])
  
  // Permission checking functions
  const hasPermission = useCallback((permission: Permission): boolean => {
    if (!state.user) return false
    
    const rolePermissions = ROLE_PERMISSIONS[state.user.role] || []
    return rolePermissions.includes(permission) || 
           (state.user.permissions?.includes(permission) ?? false)
  }, [state.user])
  
  const hasAnyPermission = useCallback((permissions: Permission[]): boolean => {
    return permissions.some(permission => hasPermission(permission))
  }, [hasPermission])
  
  const hasRole = useCallback((role: UserRole | UserRole[]): boolean => {
    if (!state.user) return false
    
    if (Array.isArray(role)) {
      return role.includes(state.user.role)
    }
    return state.user.role === role
  }, [state.user])
  
  const canAccessRoute = useCallback((path: string): boolean => {
    if (!state.user) {
      return !path.startsWith('/admin')
    }
    
    if (path.startsWith('/admin')) {
      return hasRole(['admin'])
    }
    
    return true
  }, [state.user, hasRole])
  
  const updateUser = useCallback((userData: Partial<User>): void => {
    if (!state.user) return
    dispatch({ type: 'UPDATE_USER', payload: userData })
  }, [state.user])
  
  const refreshToken = useCallback(async (): Promise<void> => {
    if (!state.token || !sessionManager.current.isSessionValid()) return
    
    try {
      const response = await fetch('/api/v2/auth.php/refresh', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-ID': sessionManager.current.getCurrentSessionId() || ''
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        if (data.success && data.token) {
          dispatch({
            type: 'LOGIN_SUCCESS',
            payload: {
              user: state.user!,
              token: data.token,
              sessionId: sessionManager.current.getCurrentSessionId()!
            }
          })
        }
      } else {
        // Refresh failed, logout
        await logout()
      }
    } catch (error) {
      console.error('Token refresh failed:', error)
      await logout()
    }
  }, [state.token, state.user, logout])
  
  // Auto refresh token - refresh more frequently to keep session alive
  useEffect(() => {
    if (state.isAuthenticated && state.token) {
      const interval = setInterval(() => {
        refreshToken()
      }, 10 * 60 * 1000) // Refresh every 10 minutes to ensure session stays active
      
      return () => clearInterval(interval)
    }
  }, [state.isAuthenticated, state.token, refreshToken])
  
  const contextValue: AuthContextType = {
    ...state,
    login,
    logout,
    hasPermission,
    hasAnyPermission,
    hasRole,
    canAccessRoute,
    updateUser,
    refreshToken,
    // OTP related
    otpState,
    verifyOTP,
    cancelOTP
  }
  
  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  )
}

// Custom hook to use auth context
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (context === undefined) {
    console.error('❌ useAuth: AuthContext is undefined - useAuth must be used within an AuthProvider')
    throw new Error('useAuth must be used within an AuthProvider')
  }
  console.log('✅ useAuth: AuthContext found, login function available:', typeof context.login)
  return context
}

// Export types
export type { AuthContextType }